/**!
 *  Tailored content widget.
 *  Author: Bjorn Tollstrom <bjorn@rodolfo.se>
 */

import React from "react";
import Widget from "../../widget.js";
import "./tailoredContent.scss";
import API from "Class/API";
import Fuse from "Class/Fuse";
import {ArrayClone, ObjectCompare, RandomToken, StripTags} from "Functions";
import ContentTypeField from "Components/UI/Field/ContentTypeField";
import IconItem from "Components/UI/IconItem";
import Link from "Components/UI/Link";
import Preview from "Components/Layout/Preview";
import Spinner from "Components/Feedback/Spinner";
import WidgetWrapper from "Components/UI/WidgetWrapper";

class WidgetTailoredContent extends Widget
{
    constructor(props)
    {
        super(props);
        this.Fields = this.SetFields({
            title:
            {
                default: "For you",
                insert: true,
                label: "Title",
                reset: true,
                type: "text",
                translate: true
            },
            showFilter:
            {
                label: "Show Filter",
                type: "checkbox",
                default: true
            },
            availableContent:
            {
                label: "Options in Content Filter",
                multiple: true,
                type: "contenttype",
                default: {contentType: [
                    "Content::Article",
                    "ScormCourse",
                    "ExternalLink",
                    "Video"
                ]}
            },
            filterNotice:
            {
                label: "Showing the filter allows the user to change the search query and content type filter.",
                type: "notice"
            },
            query:
            {
                label: "Initial Query",
                type: "text",
                placeholder: "Enter your filter query..."
            },
            content:
            {
                label: "Initial Content Filter",
                multiple: true,
                type: "contenttype",
                default: {contentType: [
                    "Content::Article",
                    "ScormCourse",
                    "ExternalLink",
                    "Video"
                ]}
            },
            community:
            {
                label: "Communities",
                placeholder: "Search for communities...",
                multiple: true,
                type: "content",
                types: ["community"]
            },
            communityNotice:
            {
                displayIf: ["community", "==", 0],
                label: "If no communities are specified, the content for the current community will be loaded.",
                type: "notice"
            },
            sorting:
            {
                label: "Sort by",
                options: {
                    relevance: "Relevance",
                    created_at: "Latest",
                    points: "Most Popular"
                },
                default: 'points',
                type: 'select'
            }
        }, {
            backgroundColor: "transparent",
            textColor: "black"
        }, true);
        this.LoadToken = false;
        this.Mounted = false;
        this.Name = "Tailored Content";
        this.Timeout = null;
        this.Translate =
        {
            filter_multiple: "Filter for %n types",
            filter_multiple_query: "Filter for %n types and \"%q\"",
            filter_single: "Filter for %n type",
            filter_single_query: "Filter for %n type and \"%q\"",
            filter_none: "No filter",
            filter_query: "Filter for \"%q\"",
            more: "Show more",
            nocontent: "No content",
            nomore: "No more content",
            query: "Query",
            query_placeholder: "Enter your filter query..."
        };
        this.state =
        {
            done: false,
            error: false,
            filter: null,
            content: [],
            loading: false,
            page: 0,
            translations: []
        };
    }

    /**
     * Load tailored content on mount.
     * @return void
     */

    componentDidMount()
    {
        const {availableContent, content, query} = this.Content();
        this.Mounted = true;
        this.SetFilter(content || availableContent, query, () => this.Reload());
    }

    /**
     * Reload tailored content if the community is changed.
     * @return void
     */

    componentDidUpdate(prevProps)
    {
        const {content: c1} = this.props;
        const {content: c2} = prevProps;
        const {content: cf1, community: cm1, query: q1, sorting: s1} = c1;
        const {content: cf2, community: cm2, query: q2, sorting: s2} = c2;
        const {contentType: ct1} = cf1 || {}
        const {contentType: ct2} = cf2 || {}

        if (!ObjectCompare(ct1, ct2) || !ObjectCompare(cm1, cm2) || q1 !== q2 || s1 !== s2)
        {
            this.Load(cm1, ct1, q1, s1, true);
        }
    }

    /**
     * Register unmount.
     * @return void
     */

    componentWillUnmount()
    {
        this.Mounted = false;
    }

    /**
     * Output a content item.
     * @param object content - Content object.
     * @param integer index - Item index in list.
     * @return JSX - The content item.
     */

    Item = (content, index) =>
    {
        const {translations} = this.state;
        const {externalLink} = this.Content();
        const {description, id, preview_path, title, type} = content;
        const {description: translatedDescription, title: translatedTitle} = this.ShouldTranslate() ? translations[index] || {} : {};
        const Url = Fuse.ContentUrl(id, type);
        const PreviewUrl = preview_path ? Fuse.Url(preview_path.substr(1), false) : "";
        return (
            <Link
                className="WidgetTailoredContentItem"
                key={`${index}.${id}`}
                href={Url}
                target={externalLink ? "_blank" : "_top"}
            >
                <Preview
                    className="ItemPreview"
                    content={type}
                    image={PreviewUrl}
                />
                <div className="ItemContent">
                    <div className="ItemName">{translatedTitle || title}</div>
                    <div className="ItemInfo">
                        {StripTags(translatedDescription || description)}
                    </div>
                </div>
            </Link>
        )
    }

    /**
     * Load the leaderboard.
     * @param array communities - Community ids.
     * @param array contentFilter - Content filter.
     * @param string query - Filter query.
     * @param boolean reset - Reset pagination.
     * @return void
     */

    Load = (communities = [], contentFilter = [], query = "", sorting = "relevance", reset = false) =>
    {
        const {context, contextId} = this.props;
        const {content, page, translations} = this.state;
        const FullFilter = Array.from(this.Fields.availableContent.default.contentType);
        const Current = context === "community" ? contextId : 0;
        const Communities = communities.map(item => item[0]).filter(id => typeof id === "number" && id > 0);
        const ContentFilter = (contentFilter.length > 0 ? contentFilter : FullFilter).filter(type => !!type);
        const Page = reset ? 1 : page || 1;
        const Token = this.LoadToken = RandomToken();
        this.setState({error: false, loading: true});
        Fuse.Request('search',
        {
            class_names: ContentFilter.join(","),
            community_ids: Communities.length > 0 ? Communities.join(",") : Current,
            page: Page,
            q: query,
            sorting: sorting !== "relevance" ? sorting : ""
        }, async (response) =>
        {
            if (!this.Mounted || Token !== this.LoadToken)
            {
                return;
            }
            const {last_page, items} = response || {};
            if (!items)
            {
                this.setState({error: true, loading: false});
            }
            else
            {
                const Content = reset ? items : ArrayClone(content).concat(items);
                const NewTranslations = await this.TranslateItems(items);
                const Translations = reset ? NewTranslations : ArrayClone(translations).concat(NewTranslations);
                this.setState({
                    done: last_page,
                    content: Content,
                    loading: false,
                    page: Page + 1,
                    translations: Translations
                });
            }
        });
    }

    OnChangeFilter = (e, value, key) =>
    {
        const {filter} = this.state;
        switch (key)
        {
            case 'contentType':
                filter.contentType = value.filter(c => !!c);
                break;
            case 'query':
                filter.query = value;
                break;
            default:
                return;
        }
        this.setState({error: false, filter, loading: true}, () =>
        {
            clearTimeout(this.Timeout);
            this.Timeout = setTimeout(() =>
            {
                if (!this.Mounted)
                {
                    return;
                }
                const {contentType, query} = filter;
                const {community, sorting} = this.Content();
                this.Load(community, contentType, query, sorting, true);
            }, 300);
        });
    }

    /**
     * Reload tailored content.
     * @param integer community - Community id.
     * @return void
     */

    Reload = () =>
    {
        const {community, content, query, sorting} = this.Content();
        const {contentType} = content || {};
        this.Load(community, contentType, query, sorting);
    }

    SetFilter = (content, query, callback) =>
    {
        const Filter = content || {contentType: []};
        Filter.query = query;
        this.setState({filter: Filter}, callback);
    }

    SetFilterLabel = (types, {query}) =>
    {
        let format = '';
        if (types.length > 1 && query)
        {
            format = this.GetTranslation("filter_multiple_query");
        }
        else if (types.length > 0 && query)
        {
            format = this.GetTranslation("filter_single_query");
        }
        else if (types.length > 1)
        {
            format = this.GetTranslation("filter_multiple");
        }
        else if (types.length > 0)
        {
            format = this.GetTranslation("filter_single");
        }
        else if (query)
        {
            format = this.GetTranslation("filter_query");
        }
        else {
            format = this.GetTranslation("filter_none");
        }
        return format.replace('%n', types.length).replace('%q', query && query.length > 18 ? query.substr(0, 15) + "..." : query || '');
    }

    /**
     * Translate items (if translation is active).
     * @param array items - Content items.
     * @return promise array - Translations.
     */

    TranslateItems = async (items) =>
    {
        const {translate} = this.props.content;
        if (!translate)
        {
            return [];
        }
        const {translations} = await API.RequestAsync("translations/contents", {contents: items});
        return translations || [];
    }

    render()
    {
        const WidgetContent = this.Content();
        const {active, hover} = this.props;
        const {done, filter, loading, content} = this.state;
        const {availableContent, showFilter, title} = WidgetContent;
        const CA = this.ClassNames(["WidgetTailored"]);
        if (active)
        {
            CA.push("Active");
        }
        if (hover)
        {
            CA.push("Hover"); 
        }
        const Content = [];
        const Items = [];
        if (content && content.length)
        {
            content.forEach((c, i) =>
            {
                Items.push(this.Item(c, i));
            });
        }
        if (!Items.length && loading)
        {
            Content.push(<Spinner
                className="WidgetSpinner"
                key="spinner"
                overlay={true}
            />);
        }
        else if (!Items.length)
        {
            Content.push(<div
                className="WidgetEmpty"
                key="empty"
            >{this.GetTranslation("nocontent")}</div>);
        }
        else
        {
            Content.push(<div
                className="WidgetTailoredContentItems"
                key="items"
            >{Items}</div>);
            Content.push(<IconItem
                className="WidgetTailoredContentButton"
                disabled={done}
                feather={done ? "StopCircle" : "PlusCircle"}
                key="fetch"
                label={done ? this.GetTranslation("nomore") : this.GetTranslation("more")}
                loading={loading}
                onClick={this.Load}
            />);
        }

        return (
            <div className={CA.join(" ")} ref={widget => this.RefWidget = widget} style={this.Style()}>
                {this.Toolbar()}
                <WidgetWrapper
                    {...WidgetContent}
                    className="WidgetTailoredContentWrapper"
                    title={title}
                    toolbar={showFilter && <ContentTypeField
                        additionalFields={{
                            query: {
                                label: this.GetTranslation("query"),
                                type: "text",
                                placeholder: this.GetTranslation("query_placeholder")
                            }
                        }}
                        className="WidgetTailoredContentFilter"
                        multiple={true}
                        onChange={this.OnChangeFilter}
                        selectedLabel={this.SetFilterLabel}
                        types={availableContent?.contentType}
                        value={filter}
                        version="v2"
                    />}
                >
                    {Content}
                </WidgetWrapper>
            </div>
        );
    }
}

export default WidgetTailoredContent;